// Web Push subscription helper — registers SW, requests permission, subscribes,
// and POSTs the subscription to the backend.
import { BACKEND_URL as API } from "@/lib/api";

function urlBase64ToUint8Array(base64String) {
  const padding = "=".repeat((4 - (base64String.length % 4)) % 4);
  const base64 = (base64String + padding).replace(/-/g, "+").replace(/_/g, "/");
  const raw = window.atob(base64);
  const out = new Uint8Array(raw.length);
  for (let i = 0; i < raw.length; i++) out[i] = raw.charCodeAt(i);
  return out;
}

export function pushSupported() {
  return (
    typeof window !== "undefined" &&
    "serviceWorker" in navigator &&
    "PushManager" in window &&
    "Notification" in window
  );
}

export async function ensureServiceWorker() {
  if (!("serviceWorker" in navigator)) throw new Error("Service worker unsupported");
  let reg = await navigator.serviceWorker.getRegistration("/");
  if (!reg) {
    reg = await navigator.serviceWorker.register("/service-worker.js", { scope: "/" });
  }
  await navigator.serviceWorker.ready;
  return reg;
}

export async function currentPushStatus() {
  if (!pushSupported()) return { supported: false, permission: "unsupported", subscribed: false };
  const permission = Notification.permission;
  let subscribed = false;
  try {
    const reg = await navigator.serviceWorker.getRegistration("/");
    if (reg) {
      const sub = await reg.pushManager.getSubscription();
      subscribed = !!sub;
    }
  } catch (e) {
    /* ignore */
  }
  return { supported: true, permission, subscribed };
}

export async function subscribePush() {
  if (!pushSupported()) throw new Error("Push is not supported on this browser.");
  if (Notification.permission === "denied") {
    throw new Error("Notifications are blocked. Enable them in your browser site settings.");
  }
  const permission =
    Notification.permission === "granted"
      ? "granted"
      : await Notification.requestPermission();
  if (permission !== "granted") throw new Error("Permission was not granted.");

  const reg = await ensureServiceWorker();

  // Fetch VAPID public key
  const keyResp = await fetch(`${API}/api/push/public-key`);
  const { publicKey } = await keyResp.json();
  if (!publicKey) throw new Error("Server has no VAPID public key configured.");

  let sub = await reg.pushManager.getSubscription();
  if (!sub) {
    sub = await reg.pushManager.subscribe({
      userVisibleOnly: true,
      applicationServerKey: urlBase64ToUint8Array(publicKey),
    });
  }

  const subJson = sub.toJSON();
  const resp = await fetch(`${API}/api/push/subscribe`, {
    method: "POST",
    credentials: "include",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify({
      endpoint: subJson.endpoint,
      keys: { p256dh: subJson.keys.p256dh, auth: subJson.keys.auth },
      platform: navigator.platform || null,
    }),
  });
  if (!resp.ok) {
    const text = await resp.text();
    throw new Error(`Server rejected subscription: ${text}`);
  }
  return sub;
}

export async function unsubscribePush() {
  if (!pushSupported()) return;
  const reg = await navigator.serviceWorker.getRegistration("/");
  if (!reg) return;
  const sub = await reg.pushManager.getSubscription();
  if (!sub) return;
  const endpoint = sub.endpoint;
  await sub.unsubscribe();
  await fetch(`${API}/api/push/subscribe`, {
    method: "DELETE",
    credentials: "include",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify({ endpoint }),
  });
}

export async function testPush() {
  const resp = await fetch(`${API}/api/push/test`, {
    method: "POST",
    credentials: "include",
  });
  if (!resp.ok) throw new Error("Test push failed");
  return resp.json();
}
